import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { TabPillItem } from '../components/TabPillRow';

// A page can register a contextual tab set for the floating bottom nav.
// While the registration is live, BottomNav renders these pills instead of
// the default app-level 5 slots. Unregistering restores the default.
export type ContextualNav = {
  items: TabPillItem<string>[];
  active: string;
  onChange: (id: string) => void;
  ariaLabel?: string;
};

type BottomNavCtx = {
  contextual: ContextualNav | null;
  setContextual: (nav: ContextualNav | null) => void;
};

const NavContext = createContext<BottomNavCtx>({
  contextual: null,
  setContextual: () => { /* no-op default */ },
});

export const BottomNavProvider = ({ children }: { children: ReactNode }) => {
  const [contextual, setContextual] = useState<ContextualNav | null>(null);
  const value = useMemo(() => ({ contextual, setContextual }), [contextual]);

  // Mirror active mode onto <body> so CSS can pad content clear of the
  // floating pill on desktop (mobile padding is unconditional in the layout).
  useEffect(() => {
    if (contextual) {
      document.body.classList.add('has-contextual-nav');
      return () => document.body.classList.remove('has-contextual-nav');
    }
    return undefined;
  }, [contextual]);

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
};

export const useBottomNavContext = () => useContext(NavContext);

// Called by a page to register a contextual tab set for the duration of the
// mount. Clears automatically on unmount so unrelated screens fall back to the
// app-level nav.
export const useContextualBottomNav = <T extends string>(
  items: TabPillItem<T>[],
  active: T,
  onChange: (id: T) => void,
  ariaLabel?: string,
) => {
  const { setContextual } = useBottomNavContext();

  // Stable onChange wrapper so the effect below doesn't re-fire on every render
  const handleChange = useCallback((id: string) => onChange(id as T), [onChange]);

  useEffect(() => {
    setContextual({
      items: items as TabPillItem<string>[],
      active,
      onChange: handleChange,
      ariaLabel,
    });
    return () => setContextual(null);
    // Rebind whenever the tab set, active id, or label changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items.map(i => ({ id: i.id, count: i.count, hidden: i.hidden, disabled: i.disabled, accent: i.accent }))), active, handleChange, ariaLabel]);
};
